// Copyright (c) 2024 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, Pressable, StyleSheet, View, Modal, Platform, Image, useWindowDimensions } from 'react-native';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Text } from './Themed';
import { useUser } from '@/app/user-context';
import { useRouter } from 'expo-router';
import { buildLogoutUrl, POST_LOGOUT_REDIRECT, revokeRefreshToken } from '@/auth/oidc';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import {
  getActiveClaimSessionSync,
  loadClaimSession,
  setActiveClaimSession,
  subscribeClaimSession,
  type ActiveClaimSession,
} from '@/services/claimSession';
import { appendClaimQuery } from '@/services/claimHttp';
import appColors from '@/theme/colors';

type HeaderUserProps = {
  onOpenBugReport?: () => void;
  compact?: boolean;
  hideProfileAndFriends?: boolean;
};

export function HeaderUser({ onOpenBugReport, compact = false, hideProfileAndFriends = false }: HeaderUserProps) {
  const { user, setUser } = useUser();
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusItems, setStatusItems] = useState<Array<{ service: string; image: string; error?: string }>>([]);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [claimSession, setClaimSession] = useState<ActiveClaimSession | null>(() => getActiveClaimSessionSync());
  const router = useRouter();
  const { t } = useI18n();
  const { width } = useWindowDimensions();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const hideName = compact || (Platform.OS === 'web' && width > 0 && width < 390);
  const displayName = ((user?.forename ?? '').trim() || (claimSession?.sourcePlayerName ?? '').trim());
  const initials = useMemo(() => {
    if (displayName.length > 0) {
      const parts = displayName.split(/\s+/).filter(Boolean);
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
    }
    const idBase = (user?.id ?? claimSession?.sourcePlayerId ?? '').replace(/[^a-zA-Z0-9]/g, '');
    return (idBase.slice(0, 2) || '?').toUpperCase();
  }, [claimSession?.sourcePlayerId, displayName, user?.id]);
  const onProfile = () => {
    setOpen(false);
    router.push('/profile');
  };

  const onFriends = () => {
    setOpen(false);
    router.push('/friends');
  };

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
        }
      });

    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (!cancelled) {
        setClaimSession(nextClaimSession);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user?.token || !user?.id) {
      setAvatarUrl(null);
      setPlayerId(null);
      return;
    }
    const controller = new AbortController();
    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
          signal: controller.signal,
        });
        if (!response.ok) return;
        const payload = (await response.json()) as {
          id?: string;
          imageThumbKey?: string | null;
          imageKey?: string | null;
        };
        if (controller.signal.aborted) return;
        setPlayerId(payload.id ?? null);
        const resolved = payload.imageThumbKey ?? payload.imageKey ?? null;
        setAvatarUrl(resolved);
      } catch {
        if (!controller.signal.aborted) {
          setAvatarUrl(null);
          setPlayerId(null);
        }
      }
    };
    load();
    return () => {
      controller.abort();
    };
  }, [apiBaseUrl, user?.id, user?.token]);

  useEffect(() => {
    if (user?.token || !claimSession?.sourcePlayerId || claimSession?.sourcePlayerName) {
      return;
    }

    const controller = new AbortController();
    const loadClaimedPlayerName = async () => {
      try {
        const response = await fetch(
          appendClaimQuery(
            `${apiBaseUrl}/api/players/by-ids?ids=${encodeURIComponent(claimSession.sourcePlayerId)}`,
            claimSession
          ),
          { signal: controller.signal }
        );
        if (!response.ok) {
          return;
        }

        const payload = (await response.json()) as Array<{
          id?: string;
          name?: string | null;
          displayName?: string | null;
        }>;
        if (controller.signal.aborted) {
          return;
        }

        const resolvedPlayer = payload.find((entry) => entry.id === claimSession.sourcePlayerId) ?? payload[0];
        const resolvedName = (resolvedPlayer?.name ?? resolvedPlayer?.displayName ?? '').trim();
        if (!resolvedName) {
          return;
        }

        const nextClaimSession = {
          ...claimSession,
          sourcePlayerName: resolvedName,
        };
        setClaimSession(nextClaimSession);
        void setActiveClaimSession(nextClaimSession);
      } catch {
        // Best-effort fallback for legacy claim sessions without a stored name.
      }
    };

    void loadClaimedPlayerName();
    return () => {
      controller.abort();
    };
  }, [apiBaseUrl, claimSession, user?.token]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const unsubscribe = socket.onMessage((message) => {
      const type = (message as { type?: string }).type;
      if (type !== 'players_snapshot') return;
      const payload = message as {
        players?: Array<{ id?: string; imageThumbKey?: string; imageKey?: string }>;
        Players?: Array<{ id?: string; imageThumbKey?: string; imageKey?: string }>;
      };
      const list = payload.players ?? payload.Players ?? [];
      const updated = list.find((p) => p?.id && p.id === playerId);
      if (!updated) return;
      const resolved = updated.imageThumbKey ?? updated.imageKey ?? null;
      setAvatarUrl(resolved);
    });
    return () => {
      unsubscribe();
    };
  }, [playerId, user?.token]);

  const onLogout = async () => {
    setOpen(false);
    const idTokenHint = user?.idToken ?? user?.token ?? null;
    const refreshToken = user?.refreshToken ?? null;
    setUser(null);
    router.replace('/');
    if (refreshToken) {
      try {
        await revokeRefreshToken(refreshToken);
      } catch {
        // Best-effort revoke; logout still proceeds.
      }
    }
    // Redirect to Keycloak logout endpoint to clear SSO session, then back to app.
    const logoutUrl = buildLogoutUrl(idTokenHint);
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
      window.location.href = logoutUrl;
    } else {
      await WebBrowser.openAuthSessionAsync(logoutUrl, POST_LOGOUT_REDIRECT);
    }
  };

  const onAbout = async () => {
    setOpen(false);
    setAboutOpen(true);
    setStatusLoading(true);
    setStatusError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/bff/status`, {
        headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (!response.ok) {
        throw new Error(t('menu.aboutLoadError'));
      }
      const payload = (await response.json()) as {
        services?: Array<{ service?: string; Service?: string; image?: string; Image?: string; error?: string; Error?: string }>;
      };
      const registryPrefix = '086307192996.dkr.ecr.eu-west-1.amazonaws.com/';
      const normalized =
        payload.services?.map((svc) => {
          const rawImage = svc.image ?? svc.Image ?? '';
          const image = rawImage.startsWith(registryPrefix) ? rawImage.slice(registryPrefix.length) : rawImage;
          return {
            service: svc.service ?? svc.Service ?? t('menu.aboutUnknownService'),
            image,
            error: svc.error ?? svc.Error,
          };
        }) ?? [];
      setStatusItems(normalized);
    } catch (_err) {
      setStatusError(t('menu.aboutLoadError'));
      setStatusItems([]);
    } finally {
      setStatusLoading(false);
    }
  };

  const onReportBug = () => {
    setOpen(false);
    onOpenBugReport?.();
  };

  return (
    <View style={styles.wrapper}>
      <Pressable style={[styles.container, hideName && styles.containerCompact]} onPress={() => setOpen((prev) => !prev)}>
        {avatarUrl ? (
          <Image source={{ uri: avatarUrl }} style={styles.avatar} onError={() => setAvatarUrl(null)} />
        ) : (
          <View style={styles.avatarFallback}>
            <Text style={styles.avatarFallbackText}>{initials}</Text>
          </View>
        )}
        {hideName ? null : (
          <Text style={styles.name} numberOfLines={1}>
            {displayName || t('menu.anonymous')}
          </Text>
        )}
      </Pressable>
      {open ? (
        <View style={styles.menu}>
          <View style={styles.menuLanguage}>
            <LanguageSwitcher />
          </View>
          {!hideProfileAndFriends ? (
            <Pressable style={styles.menuItem} onPress={onProfile}>
              <Text style={styles.menuItemText}>{t('menu.profile')}</Text>
            </Pressable>
          ) : null}
          {!hideProfileAndFriends ? (
            <Pressable style={styles.menuItem} onPress={onFriends}>
              <Text style={styles.menuItemText}>{t('menu.friends')}</Text>
            </Pressable>
          ) : null}
          <Pressable style={styles.menuItem} onPress={onAbout}>
            <Text style={styles.menuItemText}>{t('menu.about')}</Text>
          </Pressable>
          <Pressable style={styles.menuItem} onPress={onReportBug}>
            <Text style={styles.menuItemText}>{t('menu.reportBug')}</Text>
          </Pressable>
          <Pressable style={styles.menuItem} onPress={onLogout}>
            <Text style={styles.menuItemText}>{t('menu.logout')}</Text>
          </Pressable>
        </View>
      ) : null}
      <Modal visible={aboutOpen} transparent animationType="fade" onRequestClose={() => setAboutOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setAboutOpen(false)}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('menu.aboutTitle')}</Text>
            {statusLoading ? (
              <View style={styles.modalLoading}>
                <ActivityIndicator size="small" color="#F5F5DC" />
                <Text style={styles.modalLoadingText}>{t('menu.aboutLoading')}</Text>
              </View>
            ) : statusError ? (
              <Text style={styles.modalError}>{statusError}</Text>
            ) : statusItems.length === 0 ? (
              <Text style={styles.modalEmpty}>{t('menu.aboutEmpty')}</Text>
            ) : (
              statusItems.map((item) => (
                <View key={item.service} style={styles.modalRow}>
                  <Text style={styles.modalService}>{item.service}</Text>
                  <Text style={styles.modalValue}>
                    {item.error ? `${t('menu.aboutError')} ${item.error}` : item.image || t('menu.aboutUnknownImage')}
                  </Text>
                </View>
              ))
            )}
            <Pressable style={styles.modalClose} onPress={() => setAboutOpen(false)}>
              <Text style={styles.modalCloseText}>{t('menu.close')}</Text>
            </Pressable>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative',
    minWidth: 0,
    maxWidth: '100%',
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 4,
    minWidth: 0,
    maxWidth: '100%',
  },
  containerCompact: {
    paddingHorizontal: 2,
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: 6,
  },
  avatarFallback: {
    width: 38,
    height: 38,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    color: appColors.text.heading,
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.8,
  },
  name: {
    fontSize: 14,
    fontWeight: '600',
    color: '#F5F5DC',
    flexShrink: 1,
    maxWidth: 96,
  },
  menu: {
    position: 'absolute',
    top: 28,
    right: 0,
    maxWidth: 220,
    backgroundColor: '#07200D',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#1A3A20',
    ...Platform.select({
      web: {
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)',
      },
      default: {
        shadowColor: '#C8C8B0',
        shadowOpacity: 0.25,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
        elevation: 4,
      },
    }),
    minWidth: 120,
  },
  menuLanguage: {
    paddingHorizontal: 8,
    paddingTop: 8,
    alignItems: 'flex-start',
  },
  menuItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  menuItemText: {
    color: '#F5F5DC',
    fontWeight: '600',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.18)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#07200D',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#F5F5DC',
    marginBottom: 12,
  },
  modalLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
  },
  modalLoadingText: {
    color: '#F5F5DC',
    fontSize: 13,
  },
  modalError: {
    color: '#fca5a5',
    fontSize: 13,
    marginBottom: 8,
  },
  modalEmpty: {
    color: '#F5F5DC',
    fontSize: 13,
    marginBottom: 8,
  },
  modalRow: {
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#1A3A20',
  },
  modalService: {
    color: '#8ecf9a',
    fontSize: 12,
    fontWeight: '700',
  },
  modalValue: {
    color: '#F5F5DC',
    fontSize: 12,
    marginTop: 2,
  },
  modalClose: {
    marginTop: 12,
    alignSelf: 'flex-end',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: '#1A3A20',
  },
  modalCloseText: {
    color: '#F5F5DC',
    fontSize: 12,
    fontWeight: '600',
  },
});
